import {
  Component,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { Router } from "@angular/router";
import { DOCUMENT } from "@angular/common";
import { CookieService } from "ngx-cookie-service";
import { TranslateService } from "@ngx-translate/core";
import { LanguageService } from "../../core/services/language.service";
import { AuthService } from "../../core/services/auth.service";
import { CartCountService } from "../../core/services/cart-count.service";
import { NotificacionesService } from "../../core/services/notificaciones.service";
import type { Notificacion } from "../../core/services/notificaciones.model";
import { NOTIFICACION_ICON } from "../../core/services/notificaciones.model";
import { environment } from "src/environments/environment";
import { Subscription } from "rxjs";

type LangItem = { text: string; flag: string; lang: string };

@Component({
  selector: "app-topbar",
  templateUrl: "./topbar.component.html",
  styleUrls: ["./topbar.component.scss"],
})
export class TopbarComponent implements OnInit, OnDestroy {
  private element!: HTMLElement;
  private sub = new Subscription();
  cartCount = 0;
  openMobileMenu = false;

  // Notificaciones (campanita)
  unreadCount = 0;
  notificaciones: Notificacion[] = [];
  loadingNotificaciones = false;
  readonly notifIcon = NOTIFICACION_ICON;

  cookieValue = "en";
  flagvalue = "assets/images/flags/us.jpg";
  countryName = "English";

  listLang: LangItem[] = [
    { text: "English", flag: "assets/images/flags/us.jpg", lang: "en" },
    { text: "Spanish", flag: "assets/images/flags/spain.jpg", lang: "es" },
    { text: "German", flag: "assets/images/flags/germany.jpg", lang: "de" },
    { text: "Italian", flag: "assets/images/flags/italy.jpg", lang: "it" },
    { text: "Russian", flag: "assets/images/flags/russia.jpg", lang: "ru" },
  ];

  @Output() settingsButtonClicked = new EventEmitter<void>();
  @Output() mobileMenuButtonClicked = new EventEmitter<void>();

  // ✅ fallback
  readonly defaultAvatar = "assets/images/users/avatar-1.jpg";

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private router: Router,
    private auth: AuthService,
    private cartCountService: CartCountService,
    private notificacionesService: NotificacionesService,
    public languageService: LanguageService,
    public translate: TranslateService,
    private cookies: CookieService
  ) {}

  ngOnInit(): void {
    this.openMobileMenu = false;
    this.element = this.document.documentElement;

    // idioma
    const saved = this.cookies.get("lang");
    const found = this.listLang.find((x) => x.lang === saved);

    if (found) {
      this.cookieValue = found.lang;
      this.countryName = found.text;
      this.flagvalue = found.flag;
    } else {
      this.cookieValue = "en";
      this.countryName = "English";
      this.flagvalue = "assets/images/flags/us.jpg";
    }

    // ✅ (opcional) si tu AuthService expone observable, recarga al cambiar usuario
    const anyAuth: any = this.auth as any;
    if (anyAuth.user$?.subscribe) {
      this.sub.add(anyAuth.user$.subscribe());
    }

    // Carrito en navbar (solo para CLIENTE)
    if (this.isCliente) {
      this.sub.add(
        this.cartCountService.count$.subscribe((n) => (this.cartCount = n)),
      );
      this.cartCountService.refresh();
    }

    // Notificaciones: para cualquier usuario autenticado
    this.sub.add(
      this.notificacionesService.unreadCount$.subscribe(
        (n) => (this.unreadCount = n),
      ),
    );
    this.notificacionesService.startPolling();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.notificacionesService.stopPolling();
  }

  // =========================
  // Notificaciones (campanita)
  // =========================
  loadNotificaciones(): void {
    this.loadingNotificaciones = true;
    this.notificacionesService.list(1, 10).subscribe({
      next: (res) => {
        this.notificaciones = res.items ?? [];
        this.loadingNotificaciones = false;
      },
      error: () => {
        this.loadingNotificaciones = false;
      },
    });
  }

  onNotifDropdownOpenChange(open: boolean): void {
    if (open) this.loadNotificaciones();
  }

  abrirNotificacion(n: Notificacion): void {
    if (!n.read) {
      this.notificacionesService.markRead(n.id).subscribe({
        next: () => this.notificacionesService.decrementUnread(),
      });
      n.read = true;
    }
    if (n.link) this.router.navigateByUrl(n.link);
  }

  marcarTodasLeidas(event: Event): void {
    event.stopPropagation();
    this.notificacionesService.markAllRead().subscribe({
      next: () => {
        this.notificaciones = this.notificaciones.map((n) => ({
          ...n,
          read: true,
        }));
        this.notificacionesService.setUnreadCount(0);
      },
    });
  }

  timeAgo(iso: string): string {
    const diffMs = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return "Justo ahora";
    if (mins < 60) return `Hace ${mins} min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `Hace ${hours} h`;
    const days = Math.floor(hours / 24);
    return `Hace ${days} d`;
  }

  setLanguage(item: LangItem): void {
    this.countryName = item.text;
    this.flagvalue = item.flag;
    this.cookieValue = item.lang;

    this.languageService.setLanguage(item.lang);
    this.cookies.set("lang", item.lang);
  }

  toggleRightSidebar(): void {
    this.settingsButtonClicked.emit();
  }

  toggleMobileMenu(event: Event): void {
    event.preventDefault();
    this.mobileMenuButtonClicked.emit();
  }

  logout(): void {
    this.auth.logout();
    this.notificacionesService.reset();
    this.router.navigateByUrl("/account/auth/login");
  }

  async fullscreen(): Promise<void> {
    this.document.body.classList.toggle("fullscreen-enable");

    if (!this.document.fullscreenElement) {
      await this.element.requestFullscreen?.();
    } else {
      await this.document.exitFullscreen?.();
    }
  }

  // =========================
  // ✅ DATA PARA LA TOPBAR
  // =========================

  /** muestra "Nombre Apellido" desde los campos del AuthUser */
  get userName(): string {
    const u = this.auth.currentUser?.() ?? null;
    if (!u) return "Usuario";

    const first = u.firstName?.trim();
    const last = u.lastName?.trim();
    const full = [first, last].filter(Boolean).join(" ").trim();

    return full || u.name || "Usuario";
  }

  get userEmail(): string {
    return this.auth.currentUser?.()?.email ?? "";
  }

  get userRole(): string {
    return this.auth.currentUser?.()?.role ?? "";
  }

  get isCliente(): boolean {
    return this.userRole === "CLIENTE";
  }

  /** ruta real de "Mi perfil" según el tipo de usuario */
  get perfilLink(): string {
    return this.isCliente ? "/c/perfil" : "/mi-perfil";
  }

  /** true mientras el usuario no tenga una foto real cargada */
  photoFailed = false;

  private rawPhoto(): string | null {
    const photo = this.auth.currentUser?.()?.avatar ?? null;
    if (!photo) return null;
    if (photo === "/uploads/users/default.png") return null;
    return photo;
  }

  /** true solo si hay una foto real que mostrar (y no falló al cargar) */
  get hasPhoto(): boolean {
    return !!this.rawPhoto() && !this.photoFailed;
  }

  get userPhoto(): string {
    const photo = this.rawPhoto();
    if (!photo) return this.defaultAvatar;

    // absoluta
    if (photo.startsWith("http")) return photo;

    // relativa: arma URL por ORIGIN (sirve aunque baseUrl tenga /api)
    const apiOrigin = new URL(environment.baseUrl).origin;
    return `${apiOrigin}${photo}`;
  }

  /** ✅ si la imagen falla, mostramos las iniciales en vez del avatar genérico */
  onUserPhotoError(ev: Event): void {
    const img = ev.target as HTMLImageElement;
    img.onerror = null;
    this.photoFailed = true;
  }

  /** Iniciales (1 o 2 letras) para mostrar mientras no haya foto */
  get userInitials(): string {
    const u = this.auth.currentUser?.() ?? null;
    const first = u?.firstName?.trim()?.[0] ?? "";
    const last = u?.lastName?.trim()?.[0] ?? "";
    const initials = `${first}${last}`.toUpperCase();
    if (initials) return initials;

    // fallback: primeras 2 letras del nombre visible, o "U" de Usuario
    const name = this.userName.trim();
    return name ? name.slice(0, 2).toUpperCase() : "U";
  }

  /** Morado del tema (mismo que los íconos de la campanita), para que todo
   *  el "branding" de la topbar use el mismo color en vez de tonos random. */
  readonly avatarColor = "#4e0f59";
}
